import * as React from "react";
import useMedia from "../hooks/useMedia";
import {LAYOUT_CONSTANTS, SMALL_ARTICLE_MIN_HEIGHT} from "../const";
import {ArticleEntity} from "../contract/article";
import ArticleFeaturedImage from "./article/ArticleFeaturedImage";
import ArticleIntroduction from "./article/ArticleIntroduction";
import ArticleMeta from "./article/ArticleMeta";
import ArticleTopics from "./article/ArticleTopics";
import ArticleTitle from "./article/ArticleTitle";
import ArticleCard from "./article/ArticleCard";
import {ContentContainer} from "./Containers";

interface Props {
  content: ArticleEntity;
  columns?: number;
  maxWidth?: number;
  priority?: number;
  loading?: boolean;
  index?: number;
  final?: boolean;
  style?: {[key: string]: number|string}
}

const {CONTENT_BASE_WIDTH, FORM_ROW_PADDING, GUTTER_SIZE} = LAYOUT_CONSTANTS;

export const LargeNewsArticle: React.FC<Props> = ({
  content,
  index = 0,
  final= false,
  columns = 1,
  maxWidth= 1,
  priority = 3,
  loading = true, // eslint-disable-line @typescript-eslint/no-unused-vars
  style,
  ...props
}): React.ReactElement => {
  const {onMobile} = useMedia();

  const width = CONTENT_BASE_WIDTH * 2;
  const columnWidth = `calc(${(1/columns) * 100}% - calc(${(columns - 1) / columns} * ${FORM_ROW_PADDING}))`; // 33% * ((3 - 1) * FORM_ROW_PADDING)
  const ratio = onMobile ? 0.25 : 0.625;
  const direction = onMobile ? 'column' : 'row';

  return (
    <ArticleCard
      content={content}
      direction={direction}
      priority={priority}
      style={{
        marginRight: (final || !(index % columns)) ? 0 : (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING),
        marginBottom: (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING),
        width: `${(1/columns) * 100}%`,
        minWidth: columnWidth,
        maxWidth: columnWidth,
        ...style
      }}
      {...props}
    >
      <ArticleFeaturedImage
        columns={onMobile ? columns : columns * 2} // opinionated
        ratio={(1/ratio)}
        maxWidth={maxWidth}
        height={Math.floor(width * ratio)}
        style={{marginRight: onMobile ? 0 : `${GUTTER_SIZE / 2}px`, maxHeight: !onMobile ? 'initial' : null}}
        className={"theme--thumbnail-border-radius"}
      />
      <ContentContainer style={{position: 'relative'}}>
        <ContentContainer
          style={!loading ? {
            padding: `calc(${FORM_ROW_PADDING} / 2)`,
            marginBottom: `calc(-${FORM_ROW_PADDING} / 2)`,
            flex: 0
          } : {}}
        >
          <ArticleMeta
            size={7}
            compact={true}
            className={"fw-600 theme--secondary-color"}
          />
          <ArticleTitle
            size={onMobile ? 5 : 3}
            style={{margin: onMobile ? `0.5rem 0 0` : `0 0 5px`}}
            className={"fw-600 theme--primary-color"}
          />
          <ArticleIntroduction
            size={onMobile ? 6 : 5}
            lines={onMobile ? 2 : undefined}
            style={{flex: 1}}
            className={"theme--primary-font-color"}
          />
          <ArticleTopics
            size={7}
            style={{marginTop: onMobile ? 0 : '1rem'}}
            className={"theme--highlight-color theme--hover-primary-color fw-600"}
          />
        </ContentContainer>
      </ContentContainer>
    </ArticleCard>
  )
};

export const LargeSpotlightNewsArticle: React.FC<Props> = ({
  content,
  index = 0,
  final= false,
  columns = 1,
  maxWidth,
  priority = 3,
  loading = true, // eslint-disable-line @typescript-eslint/no-unused-vars
  style,
  ...props
}): React.ReactElement => {
  const {onMobile} = useMedia();

  const width = CONTENT_BASE_WIDTH * 2;
  const columnWidth = `calc(${(1/columns) * 100}% - calc(${(columns - 1) / columns} * ${FORM_ROW_PADDING}))`;
  const ratio = onMobile ? 0.5 : 0.625;
  const direction = onMobile ? 'column' : 'row';

  return (
    <ArticleCard
      content={content}
      direction={direction}
      priority={priority}
      style={{
        marginRight: (final || !(index % columns)) ? 0 : (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING),
        marginBottom: (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING),
        width: `${(1/columns) * 100}%`,
        minWidth: columnWidth,
        maxWidth: columnWidth,
        ...style
      }}
      {...props}
    >
      <ArticleFeaturedImage
        columns={columns}
        maxWidth={maxWidth}
        ratio={(1/ratio)}
        height={Math.floor(width * ratio)}
        style={{maxHeight: !onMobile ? 'initial' : null}}
        className={"theme--thumbnail-border-radius spotlight"}
      >
        <ArticleTopics
          size={7}
          style={{margin: '10px 0'}}
          className={"theme--overlay-color fw-600"}
        />
        <ArticleTitle
          size={onMobile ? 4 : 3}
          style={{maxWidth: onMobile ? 'unset' : '80%'}}
          className={"fw-600 theme--overlay-color"}
        />
      </ArticleFeaturedImage>
    </ArticleCard>
  )
};

export const MediumNewsArticle: React.FC<Props> = ({
  content,
  index = 0,
  final = false,
  columns = 1,
  maxWidth,
  priority = 2,
  loading = true, // eslint-disable-line @typescript-eslint/no-unused-vars
  style,
  ...props
}): React.ReactElement => {
  const {onMobile} = useMedia();

  const width = CONTENT_BASE_WIDTH;
  const columnWidth = `calc(${(1/columns) * 100}% - calc(${(columns - 1) / columns} * ${FORM_ROW_PADDING}))`; // 33% * ((3 - 1) * FORM_ROW_PADDING)
  const ratio = 0.75;
  const direction = 'column';

  return (
    <ArticleCard
      content={content}
      direction={direction}
      priority={priority}
      style={{
        marginRight: (final || !(index % columns)) ? 0 : (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING),
        marginBottom: (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING),
        width: `${(1/columns) * 100}%`,
        minWidth: columnWidth,
        maxWidth: columnWidth,
        ...style
      }}
      {...props}
    >
      <ContentContainer>
        <ArticleFeaturedImage
          columns={columns}
          maxWidth={maxWidth}
          ratio={(1/ratio)}
          height={Math.floor(width * ratio)}
          className={"theme--thumbnail-border-radius spotlight"}
        >
          <ArticleTopics
            size={7}
            className={"theme--overlay-color fw-600"}
          />
        </ArticleFeaturedImage>
        <ContentContainer
          style={{
            position: 'relative',
            minHeight: '185px', // MAGIC
            marginTop: `calc(${FORM_ROW_PADDING} / 2)`
          }}
        >
          <ArticleMeta
            size={7}
            compact={true}
            className={"fw-600 theme--secondary-color"}
          />

          <ArticleIntroduction
            size={onMobile ? 7 : 6}
            lines={onMobile ? 3 : 8}
            style={{flex: 1, marginTop: '5px'}}
            className={"theme--primary-font-color"}
          >
            <ArticleTitle
              size={onMobile ? 6 : 5}
              className={"fw-600 theme--primary-color"}
            />
          </ArticleIntroduction>
        </ContentContainer>
      </ContentContainer>
    </ArticleCard>
  );
}

export const MediumSpotlightNewsArticle: React.FC<Props> = ({
  content,
  index = 0,
  final = false,
  columns = 1,
  maxWidth,
  priority = 2,
  loading = true, // eslint-disable-line @typescript-eslint/no-unused-vars
  style,
  ...props
}): React.ReactElement => {
  const {onMobile} = useMedia();

  const width = CONTENT_BASE_WIDTH;
  const columnWidth = `calc(${(1/columns) * 100}% - calc(${(columns - 1) / columns} * ${FORM_ROW_PADDING}))`;
  const ratio = 0.8375;
  const direction = 'column';

  return (
    <ArticleCard
      content={content}
      direction={direction}
      priority={priority}
      style={{
        marginRight: (final || !(index % columns)) ? 0 : (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING),
        marginBottom: (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING),
        width: `${(1/columns) * 100}%`,
        minWidth: columnWidth,
        maxWidth: columnWidth,
        ...style
      }}
      {...props}
    >
      <ArticleFeaturedImage
        columns={columns}
        maxWidth={maxWidth}
        ratio={(1/ratio)}
        height={Math.floor(width * ratio)}
        className={"theme--thumbnail-border-radius spotlight"}
      >
        <ArticleTopics
          size={7}
          style={{margin: '10px 0'}}
          className={"theme--overlay-color fw-600"}
        />
        <ArticleTitle
          size={onMobile ? 6 : 5}
          className={"fw-600 theme--overlay-color"}
        />
      </ArticleFeaturedImage>
    </ArticleCard>
  );
}

export const SmallNewsArticle: React.FC<Props> = ({
  content,
  index = 0,
  final= false,
  columns = 1,
  maxWidth,
  priority = 1,
  loading = true, // eslint-disable-line @typescript-eslint/no-unused-vars
  style,
  ...props
}): React.ReactElement => {
  const {onMobile} = useMedia();

  const width = CONTENT_BASE_WIDTH * 0.5;
  const columnWidth = `calc(${(1/columns) * 100}% - calc(${(columns - 1) / columns} * ${FORM_ROW_PADDING}))`;
  const direction = 'row';
  const ratio = onMobile ? 0.8 : 1;

  return (
    <ArticleCard
      content={content}
      direction={direction}
      priority={priority}
      style={{
        marginRight: (final || !(index % columns)) ? 0 : (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING),
        marginBottom: (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING),
        maxWidth: columnWidth,
        ...style
      }}
      {...props}
    >
      <ArticleFeaturedImage
        columns={5} // opinionated
        maxWidth={maxWidth}
        ratio={(1/ratio)}
        height={Math.floor(width * ratio)}
        style={{flex: 0.5, marginRight: (onMobile ? `calc(${FORM_ROW_PADDING} / 2)` : FORM_ROW_PADDING)}}
        className={"theme--thumbnail-border-radius"}
      />
      <ContentContainer
        style={{
          width: '100%',
          justifyContent: !onMobile ? 'space-between' : 'center',
          position: 'relative',
          minHeight: onMobile ? '96px' : SMALL_ARTICLE_MIN_HEIGHT,
        }}
      >
        <ArticleMeta
          size={7}
          compact={true}
          className={"fw-600 theme--secondary-color"}
        />
        <ArticleTitle
          size={onMobile ? 6 : 5}
          style={{flex: 0, marginBottom: onMobile ? 0 : '3px'}}
          className={"fw-600 theme--primary-color"}
        />
        {!onMobile ? (
          <ArticleIntroduction
            size={6}
            lines={3}
            style={{flex: 1}}
            className={"theme--primary-font-color"}
          />
        ) : null}
        <ArticleTopics
          size={7}
          className={"theme--highlight-color theme--hover-primary-color fw-600"}
        />
      </ContentContainer>
    </ArticleCard>
  );
}

export default {
  small: SmallNewsArticle,
  medium: MediumNewsArticle,
  'medium-spotlight': MediumSpotlightNewsArticle,
  large: LargeNewsArticle,
  'large-spotlight': LargeSpotlightNewsArticle,
}
